import { ethers } from "ethers";
import Web3Modal from "web3modal";
import ABI from "../contracts/NftMint.json";
import THEOS_MAINNET from "../contracts/TheosMainnet.json";
import THEOS_ROPSTEN from "../contracts/TheosRopsten.json";
import ADDRESSES from "../contracts/addresses.json";
import NETWORKS from "../contracts/networks.json";
import AVAILABLE_CHAINS from "../contracts/availableChains.json";
const { VUE_APP_ENV } = process.env;

const env = VUE_APP_ENV !== "production" ? "staging" : "production";
const networkParams = NETWORKS[env].polygon;

const CHAINS = AVAILABLE_CHAINS[env];

const supportedChainNames = [];
Object.keys(CHAINS).map(function (key) {
  supportedChainNames.push(CHAINS[key]);
});

class EthereumService {
  constructor(chainId = 1) {
    this.chainId = chainId;
    this.walletLink = {};
    this.networkProvider = {};
    this.providerOptions = {};
    this.web3Modal = new Web3Modal({
      cacheProvider: true,
      providerOptions: this.providerOptions,
      theme: "dark",
    });
  }

  parseChainId = (id) => {
    return CHAINS[id] || "Selected network is not supported.";
  };

  parseChainName = (name) => {
    return name === "Matic Mumbai" ? "mumbai" : "mainnet";
  };

  async connectToProvider() {
    this.networkProvider = await this.web3Modal.connect();
    if (!(await this.isValidNetwork())) {
      try {
        return await window.ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: networkParams.chainId }],
        });
      } catch (error) {
        await this.addPolygonNetwork();
        await this.switchToPolygonNetwork();
      }
    }
  }

  async checkCachedProvider() {
    if (this.web3Modal.cachedProvider) {
      this.networkProvider = await this.web3Modal.connect();
    } else {
      this.networkProvider = {};
    }
  }

  async onChainChanged(handleChainChange) {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      this.networkProvider.on("chainChanged", async (info) => {
        await handleChainChange(this.parseChainId(parseInt(info, 16)));
      });
    }
  }

  async onAccountChanged(handleAccountChange) {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      this.networkProvider.on(
        "accountsChanged",
        async (accounts) => await handleAccountChange(accounts[0])
      );
    }
  }

  async isProviderConnected() {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const accounts = await provider.listAccounts();
      return accounts.length > 0;
    }
    return false;
  }

  async getNetwork() {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      return await provider.getNetwork();
    }
    return {};
  }

  async getNetworkName() {
    const network = await this.getNetwork();
    return CHAINS[network.chainId] || "Unsupported network";
  }

  async isValidNetwork() {
    const network = await this.getNetwork();
    return Boolean(CHAINS[network.chainId]);
  }

  async disconnectProvider() {
    this.web3Modal.clearCachedProvider();
    if (this.networkProvider._relay?.appName === "Theos") {
      this.walletLink.disconnect();
    }
  }

  async nftMint(metadata, royalties) {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const chain = this.parseChainName(NETWORKS[env].polygon.chainName);
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(
        ADDRESSES["NftMintContract"].polygon[chain],
        ABI["abi"],
        signer
      );
      const signerAddress = await signer.getAddress();
      const data = royalties ? [[[signerAddress, royalties * 100]]] : [[]];
      const tx = await contract.mintToken(data, signerAddress, metadata);
      await tx.wait();
      return tx;
    }
    throw new Error("No network provider found!");
  }

  async fetchAddress() {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const accounts = await provider.listAccounts();
      return accounts[0];
    }
    throw new Error("No network provider found!");
  }

  async fetchBalance(address) {
    await this.checkCachedProvider();
    const balances = {};
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const balance = await provider.getBalance(address);
      balances["default"] = ethers.utils.formatEther(balance);
      const network = this.parseChainId((await provider.getNetwork()).chainId);
      let abi = null;
      switch (network) {
        case "mainnet":
          abi = THEOS_MAINNET["abi"];
          break;
        case "ropsten":
          abi = THEOS_ROPSTEN["abi"];
          break;
        default:
          abi = null;
      }
      if (abi) {
        const contract = new ethers.Contract(
          ADDRESSES["THEOS"][network],
          abi,
          provider.getSigner()
        );
        const balanceTHEOS = await contract.balanceOf(address);
        balances["THEOS"] = ethers.utils.formatEther(balanceTHEOS);
      }
      return balances;
    }
    throw new Error("No network provider found!");
  }

  async signNonce(nonce) {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const signer = provider.getSigner();
      return await signer.signMessage(nonce);
    }
    throw new Error("No network provider found!");
  }

  async sendTransaction(transaction) {
    await this.checkCachedProvider();
    if (Object.keys(this.networkProvider).length > 0) {
      const provider = new ethers.providers.Web3Provider(this.networkProvider);
      const signer = provider.getSigner();
      transaction.gasLimit = transaction.gas;
      transaction.data = transaction.input;
      delete transaction.input;
      delete transaction.gas;
      delete transaction.__typename;
      const result = await signer.sendTransaction(transaction);
      return await result.wait();
    }
    throw new Error("No network provider found!");
  }

  async addTheosToMetamask() {
    const networkName = this.getNetworkName();
    const address =
      networkName === "ropsten"
        ? ADDRESSES.THEOS.ropsten
        : ADDRESSES.THEOS.mainnet;
    return await window.ethereum.request({
      method: "wallet_watchAsset",
      params: {
        type: "ERC20",
        options: {
          address: address,
          symbol: "THEOS",
          decimals: 18,
          image: "https://theos.fi/assets/img/logo_theos.svg",
        },
      },
    });
  }

  async addPolygonNetwork() {
    return await window.ethereum.request({
      method: "wallet_addEthereumChain",
      params: [networkParams],
    });
  }

  async switchToPolygonNetwork() {
    try {
      return await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: networkParams.chainId }],
      });
    } catch (error) {
      this.addPolygonNetwork();
      this.switchToPolygonNetwork();
    }
  }
}

export default EthereumService;
